import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { MoreButtonCDS } from '@sharedCR/coreDesignSystem/Buttons';
import { DeleteIcon, EditPenIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import withLoading from 'hocs/withLoading';
import PropTypes from 'prop-types';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { IconSize } from 'constants/coreDesignSystem';
import { PendingChangesActionType } from 'constants/pendingChanges';
import usePopover from 'hooks/usePopover';

import { openConfirmationDialogAction } from 'store/global/actions';

import { highlightCustomStyles } from './ActionButton.styles';

const ActionButton = ({
  rowData,
  setEditableTableData,
  deleteTableData,
  isEditDisabled,
  isDisabled = false,
  entityType = '',
}) => {
  const dispatch = useDispatch();

  const { anchorEl, onSetAnchorElement, onClearAnchorElement } = usePopover();

  const isNewAdded = rowData.pendingChangesActionType === PendingChangesActionType.ADDED;

  const onClickEdit = useCallback(() => {
    onClearAnchorElement();

    setEditableTableData(rowData);
  }, [onClearAnchorElement, rowData, setEditableTableData]);

  const onClickRemove = useCallback(() => {
    onClearAnchorElement();

    dispatch(
      openConfirmationDialogAction({
        type: ConfirmationModalType.WARNING,
        message: `Remove credit`,
        description: `Are you sure you want to remove this credit?`,
        onAccept: () => {
          deleteTableData({ id: rowData.id, entityType });
        },
      }),
    );
  }, [deleteTableData, dispatch, onClearAnchorElement, rowData.id, entityType]);

  const items = useMemo(
    () => [
      {
        text: 'Edit',
        onClick: onClickEdit,
        icon: <EditPenIcon size={IconSize[20]} />,
        isDisabled: isEditDisabled,
      },
      {
        text: 'Delete',
        onClick: onClickRemove,
        icon: <DeleteIcon size={IconSize[20]} />,
        isDisabled: isNewAdded || rowData.isPrimary,
      },
    ],
    [onClickEdit, onClickRemove, rowData, isEditDisabled, isNewAdded],
  );

  return (
    <>
      <MoreButtonCDS
        customStyles={isNewAdded ? highlightCustomStyles : undefined}
        disabled={isDisabled}
        isHighlighted={isNewAdded}
        isSelected={!!anchorEl}
        onClick={onSetAnchorElement}
      />

      <MenuPopoverCDS anchorEl={anchorEl} items={items} onClose={onClearAnchorElement} />
    </>
  );
};

ActionButton.propTypes = {
  setEditableTableData: PropTypes.func.isRequired,
  deleteTableData: PropTypes.func.isRequired,
  isEditDisabled: PropTypes.bool.isRequired,
  rowData: PropTypes.object.isRequired,
  entityType: PropTypes.string,
  isDisabled: PropTypes.bool,
};

export default withLoading(ActionButton);
